import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../aws/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const DEFAULTS = {
  dias_vencimiento_cartera: '30',
  impresora_tipo: '80mm',
  permitir_edicion_precios: 'false',
  conexion_impresora: 'sistema',
  controlar_stock: "false",
  campos_clientes: "{}"
};

const ConfigContext = createContext(null);

export const useConfig = () => {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig debe usarse dentro de ConfigProvider');
  return ctx;
};

export const ConfigProvider = ({ children }) => {
  const { getJwtToken, isAuthenticated } = useAuth();

  const [config, setConfig] = useState(() => ({ settings: { ...DEFAULTS } }));

  // NUEVO: Estado global de la suscripción
  const [subStatus, setSubStatus] = useState({ activa: true, enGracia: false, bloqueada: false, diasRestantes: 0 });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAppData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) {
        setConfig({ settings: { ...DEFAULTS } });
        return;
      }

      // 1. Traemos los datos de la empresa y configuraciones
      const [resEmpresa, resConfig] = await Promise.all([
        axios.get(`${API_BASE_URL}/v1-empresas`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/v1-configuraciones`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const empresaData = resEmpresa.data;

      // 2. Traemos la suscripción usando el ID de la empresa que acabamos de obtener
      let fechaVencimientoReal = null;
      try {
        const resSub = await axios.get(`${API_BASE_URL}/v1-suscripciones?empresa_id=${empresaData.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // La guardamos tal cual como viene de tu API (fecha_vencimiento)
        fechaVencimientoReal = resSub.data.fecha_vencimiento;
      } catch (err) {
        console.warn("No se pudo cargar la suscripción en el contexto global", err);
      }

      // 3. Guardamos todo en el estado global
      setConfig({
        ...empresaData,
        settings: { ...DEFAULTS, ...resConfig.data }
      });

      // MAGIA: Ahora sí usamos la fecha real de la API correcta
      if (fechaVencimientoReal) {
        const hoy = new Date();
        const vencimiento = new Date(fechaVencimientoReal);
        const diffTime = vencimiento.getTime() - hoy.getTime();
        const diffDias = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        setSubStatus({
          activa: diffDias >= 0,
          porVencer: diffDias >= 0 && diffDias <= 5,
          bloqueada: diffDias < 0,
          diasRestantes: diffDias < 0 ? 0 : diffDias
        });
      }

    } catch (err) {
      setError(err.response?.data?.error || 'Error al sincronizar datos del negocio');
      setConfig({ settings: { ...DEFAULTS } });
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    if (isAuthenticated) {
      loadAppData();
    } else {
      setConfig({ settings: { ...DEFAULTS } });
      setLoading(false);
    }
  }, [isAuthenticated, loadAppData]);

  const updateConfig = useCallback(async (partial) => {
    if (!partial || Object.keys(partial).length === 0) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      const { data } = await axios.patch(`${API_BASE_URL}/v1-configuraciones`, partial, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setConfig((prev) => ({ ...prev, settings: { ...prev.settings, ...(data.config || partial) } }));
    } catch (err) {
      throw err;
    }
  }, [getJwtToken]);

  const updateEmpresa = useCallback(async (partial) => {
    if (!partial || Object.keys(partial).length === 0) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      const { data } = await axios.patch(`${API_BASE_URL}/v1-empresas`, partial, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setConfig((prev) => ({ ...prev, ...data }));
    } catch (err) {
      throw err;
    }
  }, [getJwtToken]);

  const value = {
    config,
    subStatus, // Exponemos el estado de la suscripción
    loading,
    error,
    updateConfig,
    updateEmpresa,
    refetch: loadAppData
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};